import { z } from 'zod';

// A CSS value written by Pelicargo staff. Kept as a permissive string rather than a parsed colour
// or length: the point of the override layer is to pass values straight through to a CSS custom
// property, and the browser is the authority on what is valid there.
const cssValue = z.string().trim().min(1).max(200);
const cssColor = cssValue;
const dimension = z.union([z.number(), cssValue]);

const colorScale = z
  .object(
    Object.fromEntries(
      ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map((k) => [k, cssColor]),
    ) as Record<string, typeof cssColor>,
  )
  .partial();

const textTransform = z.enum(['none', 'uppercase', 'lowercase', 'capitalize']);

const headingLevel = z.object({ fontSize: cssValue, lineHeight: cssValue, fontWeight: cssValue }).partial();

export const CustomerThemeSchema = z.object({
  colors: z.object({ primary: colorScale, secondary: colorScale }).partial().default({}),

  // Mirrors the `default` branches of customer/src/config/theme/foundations/tokens.ts.
  semantic: z
    .object({
      bgSurface: cssColor,
      bgDefault: cssColor,
      bgSubtle: cssColor,
      bgInverse: cssColor,
      textInverse: cssColor,
      borderLight: cssColor,
      borderDefault: cssColor,
      borderHover: cssColor,
      borderFocused: cssColor,
      textDefault: cssColor,
      textParagraph: cssColor,
      textSubtle: cssColor,
      textMuted: cssColor,
      textPlaceholder: cssColor,
      error: cssColor,
      success: cssColor,
      warning: cssColor,
      info: cssColor,
    })
    .partial()
    .default({}),

  // The corner-radius scale. Chakra emits these as `--chakra-radii-*`, so overriding one reshapes
  // every `borderRadius="md"`-style call site at once. Component keys (`button.borderRadius`,
  // `field.borderRadius`, `input.borderRadius`) layer on top for a single component.
  radii: z
    .object({
      none: cssValue,
      sm: cssValue,
      base: cssValue,
      md: cssValue,
      lg: cssValue,
      xl: cssValue,
      '2xl': cssValue,
      '3xl': cssValue,
      full: cssValue,
    })
    .partial()
    .default({}),

  // `url` is not a CSS variable - it is injected as a <link rel="stylesheet">, because a font
  // family is inert unless the face is actually loaded.
  fonts: z.object({ heading: cssValue, body: cssValue, url: z.string().url() }).partial().default({}),

  heading: z
    .object({
      fontFamily: cssValue,
      fontWeight: cssValue,
      letterSpacing: cssValue,
      textTransform,
      color: cssColor,
      h1: headingLevel,
      h2: headingLevel,
      h3: headingLevel,
      h4: headingLevel,
      h5: headingLevel,
      h6: headingLevel,
    })
    .partial()
    .default({}),

  button: z
    .object({
      borderRadius: cssValue,
      fontWeight: cssValue,
      letterSpacing: cssValue,
      textTransform,
      underlineColor: cssColor,
    })
    .partial()
    .default({}),

  // The SimpleField wrapper: the chrome drawn around every Simple* control.
  field: z
    .object({
      borderRadius: cssValue,
      borderWidth: cssValue,
      padding: cssValue,
      bg: cssColor,
      borderColor: cssColor,
      borderColorHover: cssColor,
      borderColorFocus: cssColor,
      focusRing: cssValue,
    })
    .partial()
    .default({}),

  // The control inside it.
  input: z.object({ fontSize: cssValue, borderRadius: cssValue, placeholderColor: cssColor }).partial().default({}),

  label: z
    .object({ fontSize: cssValue, fontWeight: cssValue, letterSpacing: cssValue, textTransform })
    .partial()
    .default({}),

  // The Yes/No pill group: `SimpleSwitch`'s track plus the `CustomRadio` style config inside it.
  // `selectedBg`/`selectedColor` were a hardcoded `gray.800`/`white` pair repeated at six places in
  // components.ts, and `optionBg` differs per variant (`inline` white, `outline` gray.200) - one
  // key each, because a tenant picking "the selected pill colour" means all of them.
  switch: z
    .object({
      bg: cssColor,
      optionBg: cssColor,
      color: cssColor,
      selectedBg: cssColor,
      selectedColor: cssColor,
      borderRadius: cssValue,
    })
    .partial()
    .default({}),

  // Icon colour that is neither text nor a surface. The info glyph is a filled disc with the `i`
  // knocked out of it, so the disc *is* the icon's colour - hence `bg` rather than `color`.
  icon: z.object({ infoBg: cssColor }).partial().default({}),

  // Layout rather than paint, so these are applied as props by a hook, not as CSS variables.
  logo: z
    .object({ width: dimension, height: dimension, fullWidth: dimension, fullHeight: dimension })
    .partial()
    .default({}),

  copy: z.record(z.string(), z.string().max(2000)).default({}),
  assets: z.record(z.string(), cssValue).default({}),
});

export type CustomerTheme = z.infer<typeof CustomerThemeSchema>;

// `Configuration.value` is a nullable scalar string, so the stored form is JSON text. Parsing is
// split from validation deliberately:
//   - `.nullish()` lets `_configuration.register`'s `safeParse(undefined)` through, so a tenant
//     with no row still resolves to the full empty shape rather than failing to register.
//   - `ctx.addIssue` + `z.NEVER` surfaces bad JSON as a ZodError with a readable message, instead
//     of a raw SyntaxError escaping into `_configuration.refresh`.
//   - `.pipe()` rather than a second `.transform()` keeps per-field error paths intact
//     (`colors.primary.500`) instead of collapsing them into one opaque message.
// Never add `.strict()`: during a rolling deploy one instance can hold a schema that lacks a key
// another instance's admin UI just wrote, and a hard failure would blank that tenant's theme.
export const customerThemeConfigValidator = (tenantId: string) =>
  z
    .string()
    .nullish()
    .transform((raw, ctx) => {
      if (!raw?.trim()) return {};
      try {
        return JSON.parse(raw) as unknown;
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Theme must be valid JSON' });
        return z.NEVER;
      }
    })
    .pipe(CustomerThemeSchema)
    .describe(`Customer theme overrides for the ${tenantId} tenant (JSON).`);
